import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  NON_REFUNDABLE_PROCESSING_FEE_NOTICE,
  NON_REFUNDABLE_PROCESSING_FEE_SHORT_NOTICE,
} from "../utils/transactionPolicyText";

/**
 * CancellationPolicyModal
 * Fetches platform cancellation policy and shows it in a modal overlay.
 * Props:
 *   open    - boolean, whether the modal is visible
 *   onClose - callback to close the modal
 *   policy  - optional pre-fetched policy object (avoids extra network call)
 */
function CancellationPolicyModal({ open, onClose, policy: propPolicy }) {
  const [policy, setPolicy] = useState(propPolicy || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    if (policy) return; // already have data

    setLoading(true);
    setError("");
    const baseURL =
      process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
    axios
      .get(`${baseURL}/api/platform-settings/cancellation-policy`)
      .then((res) => {
        setPolicy(res.data);
      })
      .catch(() => {
        setError("Unable to load cancellation policy. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open, policy]);

  // Sync if parent passes policy after initial render
  useEffect(() => {
    if (propPolicy && !policy) {
      setPolicy(propPolicy);
    }
  }, [propPolicy, policy]);

  if (!open) return null;

  const policyData = policy?.policy || {};
  const DEFAULT_CUSTOM_FEE_TIERS = [
    { minHours: 0, feePercent: 100 },
    { minHours: 24, feePercent: 75 },
    { minHours: 48, feePercent: 50 },
    { minHours: 96, feePercent: 0 },
  ];

  const buildDisplayTiers = (policyConfig) => {
    const configuredTiers = Array.isArray(policyConfig?.feeTiers)
      ? [...policyConfig.feeTiers]
      : [];

    if (configuredTiers.length > 0) {
      return configuredTiers.sort(
        (a, b) => (a.minHours || 0) - (b.minHours || 0),
      );
    }

    if (!policyConfig?.enabled || policyConfig?.policyType === "free") {
      return [{ minHours: 0, feePercent: 0 }];
    }

    if (policyConfig?.policyType === "strict") {
      return [{ minHours: 0, feePercent: 100 }];
    }

    if (policyConfig?.policyType === "custom") {
      return DEFAULT_CUSTOM_FEE_TIERS;
    }

    const typeToHours = { "24-hour": 24, "48-hour": 48, "72-hour": 72 };
    const thresholdHours =
      (policyConfig?.policyType && typeToHours[policyConfig.policyType]) ||
      policyConfig?.hoursBeforeAppointment ||
      24;
    const lateFeePercent = policyConfig?.cancellationFeePercent ?? 50;

    return [
      { minHours: 0, feePercent: lateFeePercent },
      { minHours: thresholdHours, feePercent: 0 },
    ];
  };

  const feeTiers = buildDisplayTiers(policyData);

  const formatTierLabel = (tier, index) => {
    const min = Number(tier?.minHours) || 0;
    const next = feeTiers[index + 1];
    const nextMin = next ? Number(next.minHours) : null;

    if (feeTiers.length === 1 && min === 0) {
      return (Number(tier?.feePercent) || 0) === 0
        ? "Any time before appointment"
        : "Any time before appointment";
    }

    if (nextMin !== null) {
      if (min <= 0) return `Less than ${nextMin} hrs notice`;
      return `${min} - ${nextMin} hrs notice`;
    }
    if (min <= 0) return "Less than 24 hrs notice";
    return `More than ${min} hrs notice`;
  };

  const tierSummary = feeTiers.map((tier, index) => {
    const feePercent = Math.max(0, Number(tier?.feePercent) || 0);
    const refundPercent = Math.max(0, 100 - feePercent);

    return {
      label: formatTierLabel(tier, index),
      feePercent,
      refundPercent,
    };
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Cancellation Policy"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "16px",
          padding: "28px 24px",
          maxWidth: "480px",
          width: "100%",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          position: "relative",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "20px",
          }}
        >
          <h2
            style={{ fontSize: "20px", fontWeight: "700", margin: 0, color: "#111827" }}
          >
            📋 Cancellation Policy
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "none",
              border: "none",
              fontSize: "22px",
              cursor: "pointer",
              color: "#6b7280",
              lineHeight: 1,
              padding: "0 4px",
            }}
          >
            ×
          </button>
        </div>

        {loading && (
          <p style={{ color: "#6b7280", textAlign: "center" }}>
            Loading policy…
          </p>
        )}

        {error && (
          <p style={{ color: "#dc2626", textAlign: "center" }}>{error}</p>
        )}

        {!loading && !error && (
          <>
            {/* Description */}
            <div
              style={{
                fontSize: "14px",
                color: "#374151",
                lineHeight: "1.6",
                marginBottom: "20px",
              }}
            >
              <p style={{ margin: "0 0 8px 0" }}>
                Find our cancellation fees and refunds below.
              </p>
              <p style={{ margin: 0 }}>
                Your refund depends on how much notice you give before the appointment, based on the current platform cancellation settings.
              </p>
            </div>

            <div
              style={{
                background: "#fff7ed",
                border: "1px solid #fdba74",
                borderRadius: "10px",
                padding: "12px 14px",
                marginBottom: "20px",
              }}
            >
              <p
                style={{
                  fontSize: "13px",
                  fontWeight: "700",
                  color: "#9a3412",
                  margin: "0 0 6px 0",
                }}
              >
                Important Payment Notice
              </p>
              <p
                style={{
                  fontSize: "13px",
                  color: "#7c2d12",
                  lineHeight: "1.55",
                  margin: "0 0 6px 0",
                }}
              >
                {NON_REFUNDABLE_PROCESSING_FEE_NOTICE}
              </p>
              <p
                style={{
                  fontSize: "12px",
                  color: "#9a3412",
                  lineHeight: "1.5",
                  margin: 0,
                }}
              >
                {NON_REFUNDABLE_PROCESSING_FEE_SHORT_NOTICE}
              </p>
            </div>

            {/* Fee tiers */}
            {feeTiers.length > 0 && (
              <>
                <h3
                  style={{
                    fontSize: "13px",
                    fontWeight: "700",
                    color: "#111827",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    marginBottom: "10px",
                  }}
                >
                  Cancellation Fees And Refunds
                </h3>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    marginBottom: "24px",
                  }}
                >
                  {feeTiers.map((tier, idx) => {
                    const summary = tierSummary[idx];
                    const feeLabel =
                      summary.feePercent <= 0
                        ? "No cancellation fee"
                        : `${summary.feePercent}% fee`;
                    const refundLabel = `${summary.refundPercent}% refund`;
                    return (
                      <div
                        key={`${summary.label}-${idx}`}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "10px 14px",
                          background: "#f9fafb",
                          borderRadius: "8px",
                          fontSize: "14px",
                        }}
                      >
                        <span style={{ color: "#374151" }}>{summary.label}</span>
                        <div style={{ textAlign: "right" }}>
                          <div
                            style={{
                              fontWeight: "700",
                              color:
                                summary.feePercent <= 0 ? "#059669" : "#dc2626",
                            }}
                          >
                            {feeLabel}
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#6b7280",
                              marginTop: "2px",
                            }}
                          >
                            {refundLabel}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <button
              onClick={onClose}
              style={{
                width: "100%",
                padding: "12px",
                background: "linear-gradient(135deg, #2d6da3 0%, #4a90c6 100%)",
                color: "#fff",
                border: "none",
                borderRadius: "10px",
                fontWeight: "600",
                fontSize: "15px",
                cursor: "pointer",
              }}
            >
              Got it
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default CancellationPolicyModal;
